import { ReactElement } from "react";
import { DateString } from "../DateString/DateString";
import { FormattedText } from "../FormattedText/FormattedText";
import { localize } from "client_legacy/ndaw/culture";
import { PreviousNextSlider } from "../PreviousNextSlider/PreviousNextSlider";
import { never } from "client_legacy/ndaw/errors";
import styles from "./WeekSlider.module.less";
import { Button } from "../Button/Button";
import { generateTrackingId } from "client/Misc/utils";
import {
	addDays,
	getIsoWeekNumber,
	getStartOfIsoWeek,
	getStartOfWeek,
	getWeekNumber
} from "client/DateAndTime/dateTimeUtils";
import { TimeZone } from "client/DateAndTime/timezones";
import { WeekMode } from "client/Misc/commonTypes";

export type WeekSliderProps = {
	mode?: WeekMode;
	startDate: Date;
	onChange(startDate: Date): void;
	isTodayButtonAvailable?: boolean;
	trackingId?: string;
	timezone: TimeZone;
};

export const WeekSlider = (props: WeekSliderProps): ReactElement => {
	const {
		mode = "culture",
		startDate,
		onChange,
		isTodayButtonAvailable,
		timezone,
		trackingId
	} = props;

	const changeDateHandler = (weeksToAdd: number): void => {
		const newValue: Date = addDays(
			startDate,
			weeksToAdd * 7,
			timezone
		);
		onChange(newValue);
	};

	const endDate: Date = addDays(
		startDate,
		6,
		timezone
	);

	const weekNumber: number = getWeekNumberByMode(
		startDate,
		mode,
		timezone
	);

	const todayHandler = (): void => {
		let startOfWeek: Date;
		switch(mode) {
			case "culture":
				startOfWeek = getStartOfWeek(new Date(), timezone);
				break;
			case "iso":
				startOfWeek = getStartOfIsoWeek(new Date(), timezone);
				break;
			default:
				never(mode);
		}
		onChange(startOfWeek);
	};

	return (
		<div className={styles.root}>
			{
				isTodayButtonAvailable && (
					<Button
						kind="secondary"
						text={localize("_Today")}
						onClick={todayHandler}
						trackingId={generateTrackingId(trackingId, "__today-button")}
					/>
			)}
			<div className={styles.previousNext}>
				<PreviousNextSlider
					onPrevious={() => changeDateHandler(-1)}
					onNext={() => changeDateHandler(1)}
					trackingId={generateTrackingId(trackingId, "__slider")}
				>
					<FormattedText
						text={localize("_WeekNumber")}
						thingsToReplace={[
							{
								what: "{{Number}}",
								filler: weekNumber
							}
						]}
					/> - <FormattedText
						text={localize("_StartUntilEnd")}
						thingsToReplace={[
							{
								what: "{{Start}}",
								filler: (
									<DateString
										value={startDate}
										format="DM(Y)"
										timezone={timezone}
									/>
								)
							},
							{
								what: "{{End}}",
								filler: (
									<DateString
										value={endDate}
										format="DM(Y)"
										timezone={timezone}
									/>
								)
							}
						]}
					/>
				</PreviousNextSlider>
			</div>
		</div>
	);
};
WeekSlider.displayName = "WeekSlider";

function getWeekNumberByMode(
	value: Date,
	mode: WeekMode,
	timezone: TimeZone
): number {
	switch (mode) {
		case "culture":
			return getWeekNumber(value, timezone);
		case "iso":
			return getIsoWeekNumber(value, timezone);
		default:
			never(mode);
	}
}
